import type {
  NavLink,
  Service,
  AudienceCard,
  GalleryItem,
  Testimonial,
  FooterQuickLink,
} from "../types";

export const NAV_LINKS: NavLink[] = [
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "About", href: "#about" },
  { label: "Government & Corporate", href: "#book", highlight: true },
  { label: "Contact", href: "#contact" },
];

export const TICKER_ITEMS = [
  "Balloon Design",
  "Custom Backdrops",
  "Large Format Printing",
  "Marquee Letters",
  "Corporate Events",
  "Government Programs",
  "Step & Repeat Banners",
  "Event Rentals",
];

export const TRUST_PILLS = [
  "Fully Insured",
  "SAM.gov Registered",
  "Woman-Owned",
  "Minority-Owned",
  "Free Quotes",
];

export const AUDIENCE_CARDS: AudienceCard[] = [
  {
    icon: "🎈",
    title: "Private Events",
    description:
      "Birthdays, baby showers, quinceañeras, and holiday celebrations. We handle everything from single balloon arches to full room transformations.",
    tags: [
      "Balloon Installs",
      "Backdrops",
      "Marquee Letters",
      "Custom Printing",
      "Rentals",
    ],
    footerNote: "Packages from $550",
    footerLinkLabel: "View Packages →",
    footerLinkHref: "#services",
    isPrimary: true,
  },
  {
    icon: "🏛️",
    title: "Corporate, Government & Institutional",
    description:
      "Corporate parties, brand activations, government agencies, police departments, schools, universities, and nonprofits. Vendor-ready with full documentation for procurement and compliance teams.",
    tags: [
      "Corporate Events",
      "SAM.gov Registered",
      "Capability Statement",
      "Past Performance",
      "Fully Insured",
    ],
    footerNote: "Custom quote — reach out for pricing",
    footerLinkLabel: "Vendor Inquiry →",
    footerLinkHref: "#book",
  },
];

export const SERVICES: Service[] = [
  {
    number: "01",
    title: "Balloon Design & Installations",
    description:
      "Organic garlands, arches, columns, and full ceiling installs. Custom color matching and themed builds.",
    startingFrom: "From $550",
    note: "Excl. delivery, setup & breakdown",
  },
  {
    number: "02",
    title: "Custom Backdrops & Step-and-Repeats",
    description:
      "Fabric, printed, and mixed-media backdrops built to your theme, brand, or event identity.",
    startingFrom: "From $200",
  },
  {
    number: "03",
    title: "Large Format Printing",
    description:
      "Banners, foam boards, signage, cut-outs, and print backdrops — produced in-house for fast turnaround.",
    startingFrom: "From $75",
  },
  {
    number: "04",
    title: "Full Event Décor Packages",
    description:
      "Marquee letters, balloon garlands, backdrops, and signage — the complete environment, one vendor.",
    startingFrom: "From $2,500",
    note: "Excl. delivery, setup & breakdown · 4+ hr setup required",
  },
];

export const GALLERY_ITEMS: GalleryItem[] = [
  {
    id: 1,
    src: "/smiles-marquee.PNG",
    alt: "SMILES marquee with balloon installation",
    tag: "Marquee + Balloon",
    span: "tall",
  },
  {
    id: 2,
    src: "/sesame-themed.jpg",
    alt: "Sesame Street themed event display",
    tag: "Themed Party",
  },
  {
    id: 3,
    src: "/valentines.jpg",
    alt: "Valentine's Day balloon installation",
    tag: "Balloon Install",
  },
  {
    id: 4,
    src: "/baby-russell.jpg",
    alt: "Baby Russell stacked marquee with balloon installation",
    tag: "Custom Marquee",
  },
  {
    id: 5,
    src: "/film-festival.jpg",
    alt: "Smiles Film Festival corporate event",
    tag: "Corporate Event",
  },
  {
    id: 6,
    src: "/wonderland-themed.jpg",
    alt: "Wonderland themed event display",
    tag: "Themed Party",
  },
  {
    id: 7,
    src: "/free-standing.png",
    alt: "Free standing garland installation",
    tag: "Balloon Install",
  },
  {
    id: 8,
    src: "/organic-arch.png",
    alt: "Organic arch for corporate event",
    tag: "Corporate Event",
  },
  {
    id: 9,
    src: "/grinch-setup.jpg",
    alt: "Grinch themed event display",
    tag: "Holiday Party",
  },
  {
    id: 10,
    src: "/coquette-themed.jpg",
    alt: "Coquette themed event display",
    tag: "Custom Backdrop",
  },
  {
    id: 11,
    src: "/custom-cut-out.jpg",
    alt: "SMILES custom cut-out signage",
    tag: "Custom Cut-outs & Floor Wraps",
  },
  {
    id: 12,
    src: "/focal-point.jpg",
    alt: "Focal point installation for private event",
    tag: "Focal Point",
  },
  {
    id: 13,
    src: "/full-service-event.jpg",
    alt: "Full service event display",
    tag: "Full Service",
  },
  {
    id: 14,
    src: "/welcome-sign.jpg",
    alt: "Custom welcome sign",
    tag: "Custom Welcome Sign & Balloon Garland",
  },
  {
    id: 15,
    src: "/organic-arch-tunnel.jpg",
    alt: "Organic arch tunnel for private event",
    tag: "Private Event",
  },
  {
    id: 16,
    src: "/custom-marquee1.png",
    alt: "REIGN marquee with balloon installation",
    tag: "Custom Marquee",
  },
  {
    id: 17,
    src: "/custom-marquee2.png",
    alt: "BROOKE marquee with balloon installation",
    tag: "Custom Marquee",
  },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "We booked Olóye Events for my daughter’s 1st birthday with an Alice in Onderland theme, and the decorations were absolutely amazing! Every detail was creative, from the whimsical setup to the vibrant colors and unique touches that truly brought the theme to life. It felt like we stepped right into Wonderland. Everything was beautifully done and made the day feel extra special. Highly recommend for anyone looking for a magical and memorable celebration!",
    name: "Wilimina",
    context: "Birthday Celebration · Staten Island, NY",
    avatar: "👩🏽",
  },
  {
    quote:
      "Oloye Events is nothing short of amazing. I booked them for my baby shower and the event planner brung my vision to life. Not only did she execute the event well, the attention to detail is what really made it special. Whenever I felt overwhelmed she stepped in with fun & fresh ideas. Oloye Events has a unique talent for adding special touches to their decorations. Oloye Event exceeded my expectations in every way imaginable and it’s safe to say I will be booking them for all my future events.",
    name: "Jaquelyn M.",
    context: "Birthday Celebration · New York, NY",
    avatar: "👨🏾",
  },
  {
    quote:
      "In all my years producing this festival, I’ve never encountered someone as professional, creative, and genuinely understanding. Catherine consistently goes above and beyond to transform the event into something truly special—creating an atmosphere that feels warm, elevated, and unforgettable. What sets her apart is her dedication and care. She doesn’t just execute a vision, she enhances it. Every idea I bring to her is thoughtfully developed and elevated beyond my expectations. Her attention to detail, work ethic, and commitment to excellence are unmatched.",
    name: "Emmanuel Ojo Creator, Smiles Film Festival",
    context: "Corporate Activation · New York, NY",
    avatar: "👨🏾",
  },
];

export const FOOTER_LINKS: {
  services: string[];
  clients: string[];
  quickLinks: FooterQuickLink[];
} = {
  services: [
    "Balloon Installations",
    "Custom Backdrops",
    "Large Format Printing",
    "Marquee Letters",
    "Full Event Packages",
    "Rentals",
  ],
  clients: [
    "Private Events",
    "Corporate Events",
    "Government Agencies",
    "Schools & Universities",
    "Nonprofits",
    "Police Departments",
  ],
  quickLinks: [
    {
      label: "Capability Statement ↓",
      href: "/capability_statement.pdf",
      highlight: true,
    },
    { label: "Request a Quote", href: "#contact" },
    { label: "Gallery", href: "#gallery" },
    { label: "About Us", href: "#about" },
    { label: "Contact", href: "#contact" },
  ],
};
